// VerifyBookingForm.jsx
import React, { useEffect, useMemo, useState } from "react";
import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import "../../index.css";
import { GET_BOOKINGS, RECORD_MANUAL_ONLINE_PAYMENT } from "./graphql";

/* =====================================
   Queries / Mutations
===================================== */
const GET_SERVICES = gql`
  query getAdminService($data: Filter!) {
    getAdminService(data: $data) {
      services {
        id
        name
        price
        cutPrice
        specialPrice
        firstTimePrice
        gender
        time
      }
    }
  }
`;

const VERIFY_BOOKING = gql`
  mutation verifyBooking($data: verifyBookingInput) {
    verifyBooking(data: $data) {
      status
      message
      order {
        id
        price
        totalPrice
        status
        address
        bookingDate
        timeSlot
        paymentStatus
        pincode
        paymentType
        verificationStatus
        femaleTimeSlot
        bookingId
        bookingType
        adminPriceAdjustment
        adminPriceAdjustmentReason
        couponDiscount
        codCharges
        couponCode
        amountPaid
        cashCollected
        orderDetail {
          id
          name
          serviceId
          price
          specialPrice
          firstTimePrice
          cutPrice
          count
          type
        }
        employee {
          id
          fullName
        }
        user {
          id
          fullName
          contactNumber
        }
      }
    }
  }
`;

const GET_BOOKING_AUDIT_LOGS = gql`
  query getBookingAuditLogs($orderId: ID!) {
    getBookingAuditLogs(orderId: $orderId) {
      status
      message
      audits {
        id
        orderId
        changedBy
        changedByType
        reason
        details
        createdAt
      }
    }
  }
`;

/* =====================================
   Component
   props:
     - editingData: initial order object or { id, ... }
     - onUpdate(updatedOrder)  // optional callback to notify parent about changes
     - onClose()               // optional - not required but supported
===================================== */

const SmallLabel = ({ children }) => (
  <label className="text-white text-sm block mb-1">{children}</label>
);

const SectionCard = ({ title, children, className = "" }) => (
  <div className={`bg-[#1a1a1a] text-white p-4 rounded-md ${className}`}>
    {title && (
      <h3 className="text-[#FBB034] text-lg font-bold mb-3">{title}</h3>
    )}
    {children}
  </div>
);

const VerifyBookingForm = ({ editingData, onUpdate, onClose }) => {
  // Primary data container - will be populated from backend
  const [orderData, setOrderData] = useState(null);

  // UI state
  const [selectedServices, setSelectedServices] = useState([]);
  const [manualPriceAdj, setManualPriceAdj] = useState("");
  const [manualReason, setManualReason] = useState("");
  const [adminPriceAdjustmentReason, setPriceAdjustmentReason] = useState("");
  const [status, setStatus] = useState("Completed");
  const [paymentStatus, setPaymentStatus] = useState("PENDING");
  const [paymentType, setPaymentType] = useState("COD");
  const [serviceSearchTerm, setServiceSearchTerm] = useState("");
  const [servicesList, setServicesList] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [address, setAddress] = useState("");
  const [pincode, setPincode] = useState("");
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [extraKilometers, setExtraKilometers] = useState("");

  // Manual online payment (admin QR paid outside the per-booking flow)
  const [showManualPaymentForm, setShowManualPaymentForm] = useState(false);
  const [manualPaymentAmount, setManualPaymentAmount] = useState("");
  const [manualPaymentReference, setManualPaymentReference] = useState("");
  const [manualPaymentNote, setManualPaymentNote] = useState("");

  // GraphQL hooks
  // NOTE: you used GET_BOOKINGS mutation previously to fetch single booking — keep using it if that's your pattern
  const [getBooking, { loading: bookingsLoading }] = useMutation(GET_BOOKINGS);
  const { data: servicesData, loading: servicesLoading } = useQuery(
    GET_SERVICES,
    {
      variables: {
        data: { order: "", search: "" },
      },
    },
  );
  const [getAuditLogs, { loading: auditLoading }] = useLazyQuery(
    GET_BOOKING_AUDIT_LOGS,
    {
      fetchPolicy: "network-only",
    },
  );
  const [verifyBooking, { loading: verifying }] = useMutation(VERIFY_BOOKING);
  const [recordManualOnlinePayment, { loading: recordingPayment }] =
    useMutation(RECORD_MANUAL_ONLINE_PAYMENT);

  /* -------------------
     Fetch booking & initialize form from orderData
     ------------------- */
  useEffect(() => {
    let mounted = true;
    const fetchBookings = async () => {
      try {
        const res = await getBooking({
          variables: { data: { id: editingData.id } },
        });
        const success = res?.data?.getBookings?.status === "SUCCESS";
        const booking = success ? res.data.getBookings.orders[0] : null;
        if (mounted && booking) {
          setOrderData(booking);
          // initialize editable fields from booking
          setManualPriceAdj(
            booking.adminPriceAdjustment != null
              ? String(booking.adminPriceAdjustment)
              : "",
          );
          setPaymentStatus(booking.paymentStatus || "PENDING");
          setPaymentType(booking.paymentType || "COD");
          setStatus(booking.status || "Completed");
          setAddress(booking.address || "");
          setPincode(booking.pincode || "");
          setPriceAdjustmentReason(booking.adminPriceAdjustmentReason || "");
          setExtraKilometers(
            booking.extraKilometers != null
              ? String(booking.extraKilometers)
              : "",
          );

          if (booking.orderDetail) {
            setSelectedServices(
              booking.orderDetail.map((s) => ({
                id: s.serviceId,
                name: s.name,
                count: s.count,
                price: s.price,
                type: s.type || "basic",
                specialPrice: s.specialPrice || 0,
                firstTimePrice: s.firstTimePrice || 0,
              })),
            );
          }

          // fetch audits
          const r = await getAuditLogs({ variables: { orderId: booking.id } });
          const audits = r?.data?.getBookingAuditLogs?.audits || [];
          setAuditLogs(audits);
        }
      } catch (error) {
        console.error("Booking fetch error:", error);
      }
    };

    if (editingData?.id) fetchBookings();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingData?.id]);

  /* -------------------
     Sync service list
     ------------------- */
  useEffect(() => {
    if (servicesData?.getAdminService?.services) {
      setServicesList(servicesData.getAdminService.services);
    }
  }, [servicesData]);

  useEffect(() => {
    if (orderData && orderData.orderDetail && servicesList.length > 0) {
      let standardTotal = 0;
      let firstTimeTotal = 0;

      orderData.orderDetail.forEach((s) => {
        const srv = servicesList.find((x) => x.id === s.serviceId);
        if (srv) {
          if (s.type === "free") {
            standardTotal += srv.price * Math.max(0, s.count - 1);
          } else if (s.type === "special") {
            standardTotal += srv.price * Math.max(0, s.count - 1);
            standardTotal += Number(srv.specialPrice || 0);
          } else {
            standardTotal += srv.price * s.count;
          }

          if (srv.firstTimePrice > 0) {
            firstTimeTotal += srv.price * Math.max(0, s.count - 1);
            firstTimeTotal += Number(srv.firstTimePrice);
          } else {
            if (s.type === "free") {
              firstTimeTotal += srv.price * Math.max(0, s.count - 1);
            } else if (s.type === "special") {
              firstTimeTotal += srv.price * Math.max(0, s.count - 1);
              firstTimeTotal += Number(srv.specialPrice || 0);
            } else {
              firstTimeTotal += srv.price * s.count;
            }
          }
        } else {
          standardTotal += s.price * s.count;
          firstTimeTotal += s.price * s.count;
        }
      });

      const orderBasePrice = Number(orderData.price || 0);
      if (
        firstTimeTotal !== standardTotal &&
        Math.abs(orderBasePrice - firstTimeTotal) <
          Math.abs(orderBasePrice - standardTotal)
      ) {
        setIsFirstTimeUser(true);
      }
    }
  }, [orderData, servicesList]);

  /* -------------------
     Filter services (memoized)
     ------------------- */
  const filteredServices = useMemo(() => {
    const term = (serviceSearchTerm || "").trim().toLowerCase();
    if (!term) return [];
    return servicesList.filter((s) => s.name.toLowerCase().includes(term));
  }, [serviceSearchTerm, servicesList]);

  /* -------------------
     Service add/remove
     ------------------- */
  const handleServiceAdd = (service) => {
    setSelectedServices((prev) => {
      const exists = prev.find((s) => s.id === service.id);
      if (exists)
        return prev.map((s) =>
          s.id === service.id ? { ...s, count: s.count + 1 } : s,
        );
      return [...prev, { ...service, count: 1, type: "basic" }];
    });
  };

  const handleServiceRemove = (id) => {
    setSelectedServices((prev) =>
      prev
        .map((s) => (s.id === id ? { ...s, count: s.count - 1 } : s))
        .filter((s) => s.count > 0),
    );
  };

  /* -------------------
     Totals
     ------------------- */
  const calculateNewTotal = () => {
    let total = 0;
    for (const service of selectedServices) {
      if (isFirstTimeUser && service.firstTimePrice > 0) {
        total += service.price * Math.max(0, service.count - 1);
        total += Number(service.firstTimePrice);
      } else if (service.type === "free")
        total += service.price * Math.max(0, service.count - 1);
      else if (service.type === "special") {
        total += service.price * Math.max(0, service.count - 1);
        total += Number(service.specialPrice || 0);
      } else total += service.price * service.count;
    }
    return total;
  };

  const adjustedTotal = useMemo(() => {
    const base = Number(calculateNewTotal() || 0);
    const adj = Number(manualPriceAdj || 0);
    const discount = Number(orderData?.couponDiscount || 0);
    const walletDiscount = Number(orderData?.walletAmountUsed || 0);
    const codCharges = Number(orderData?.codCharges || 0);
    const finalTotal = base + adj - discount - walletDiscount + codCharges;
    return finalTotal >= 0 ? finalTotal.toFixed(2) : "0.00";
  }, [selectedServices, manualPriceAdj, orderData]);

  /* -------------------
     Submit
     ------------------- */
  const handleSubmit = async () => {
    if (!orderData) return alert("Booking not loaded yet");

    const updatedServiceList = selectedServices.flatMap((s) =>
      Array.from({ length: s.count }).map(() => ({
        id: s.id,
        type: s.type,
        price: s.price,
        name: s.name,
      })),
    );

    const payload = {
      orderId: orderData.id,
      updatedServiceList,
      status,
      paymentStatus,
      paymentType,
      couponDiscount: orderData.couponDiscount || 0,
      reason: manualReason || "Admin verified adjustment",
      address: address || "",
      pincode: pincode || "",
      extraKilometers:
        extraKilometers !== "" ? parseFloat(extraKilometers) : undefined,
    };

    if (manualPriceAdj !== "" && Number(manualPriceAdj) !== 0) {
      payload.adminPriceAdjustment = Number(manualPriceAdj);
      payload.adminPriceAdjustmentReason =
        adminPriceAdjustmentReason || "Manual correction";
    }

    try {
      const res = await verifyBooking({ variables: { data: payload } });
      const out = res?.data?.verifyBooking;
      if (out?.status === "SUCCESS") {
        // prefer returned order if backend gives it
        const updatedOrder = out.order || null;

        if (updatedOrder) {
          setOrderData(updatedOrder);
          // refresh audit logs from backend
          const r = await getAuditLogs({
            variables: { orderId: updatedOrder.id },
          });
          setAuditLogs(r?.data?.getBookingAuditLogs?.audits || []);
          // notify parent (if provided)
          if (typeof onUpdate === "function") onUpdate(updatedOrder);
        } else {
          // fallback optimistic update (partial)
          setOrderData((prev) => ({
            ...prev,
            verificationStatus:
              payload.adminPriceAdjustment ||
              payload.paymentStatus === "Completed"
                ? "VERIFIED"
                : prev.verificationStatus,
            price: Number(adjustedTotal),
            orderDetail: selectedServices.map((s) => ({
              serviceId: s.id,
              name: s.name,
              count: s.count,
              price: s.price,
              type: s.type,
            })),
            address: payload.address,
            pincode: payload.pincode,
          }));
          if (typeof onUpdate === "function") onUpdate(orderData);
        }

        alert(out.message || "Booking verified successfully!");
        if (typeof onClose === "function") onClose();
      } else {
        alert(out?.message || "Verification failed");
      }
    } catch (err) {
      console.error("verifyBooking error:", err);
      alert("Verification failed!");
    }
  };

  /* -------------------
     Record a manual online payment (paid via the admin's own/static QR,
     captured by Razorpay but not tied to this booking automatically)
     ------------------- */
  const handleRecordManualPayment = async () => {
    if (!orderData) return;

    const amount = Number(manualPaymentAmount);
    if (!manualPaymentAmount || Number.isNaN(amount) || amount <= 0) {
      return alert("Enter a valid amount greater than 0");
    }

    try {
      const res = await recordManualOnlinePayment({
        variables: {
          data: {
            orderId: orderData.id,
            amount,
            referenceId: manualPaymentReference || undefined,
            note: manualPaymentNote || undefined,
          },
        },
      });
      const out = res?.data?.recordManualOnlinePayment;
      if (out?.status === "SUCCESS") {
        setOrderData((prev) => ({
          ...prev,
          amountPaid: out.amountPaid,
          adminRecordedAmount: out.adminRecordedAmount,
        }));
        setManualPaymentAmount("");
        setManualPaymentReference("");
        setManualPaymentNote("");
        setShowManualPaymentForm(false);

        const r = await getAuditLogs({ variables: { orderId: orderData.id } });
        setAuditLogs(r?.data?.getBookingAuditLogs?.audits || []);

        alert(
          `Payment recorded. ₹${out.amountDue} still pending for this booking.`,
        );
      } else {
        alert(out?.message || "Failed to record payment");
      }
    } catch (err) {
      console.error("recordManualOnlinePayment error:", err);
      alert("Failed to record payment!");
    }
  };

  /* -------------------
     Loading / Early returns
     ------------------- */
  if (bookingsLoading || !orderData)
    return <p className="text-gray-400">Loading booking...</p>;

  const isPaymentPending =
    orderData.verificationStatus === "PAYMENT_VERIFICATION_PENDING";
  const isVerified = orderData.verificationStatus === "VERIFIED";

  /* -------------------
     Reusable UI blocks (DRY)
     ------------------- */
  const Header = (
    <div className="bg-[#0c0b0b] p-2 rounded-md text-white">
      <p>
        <strong>Booking ID:</strong> {orderData.bookingId}
      </p>
      <p>
        <strong>Current Status:</strong> {orderData.status}
      </p>
      <p>
        <strong>Employee:</strong> {orderData.employee?.fullName || "N/A"}
      </p>
      <p>
        <strong>Address:</strong> {address || orderData.address || "N/A"}
      </p>
      <p>
        <strong>Pincode:</strong> {pincode || orderData.pincode || "N/A"}
      </p>
    </div>
  );

  const PaymentSection = (
    <div className="flex flex-wrap gap-4 items-end">
      <div>
        <SmallLabel>Payment Status:</SmallLabel>
        <select
          value={paymentStatus}
          onChange={(e) => setPaymentStatus(e.target.value)}
          className="bg-[#0c0b0b] text-white px-2 py-1 border border-gray-600 rounded"
        >
          <option value="Completed">COMPLETED</option>
          <option value="Pending">PENDING</option>
        </select>
      </div>

      <div>
        {paymentStatus === "Completed" ? (
          <>
            <SmallLabel>Payment Type:</SmallLabel>
            <select
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value)}
              className="bg-[#0c0b0b] text-white px-2 py-1 border border-gray-600 rounded"
            >
              <option value="COD">COD</option>
              <option value="Online">ONLINE</option>
            </select>
          </>
        ) : (
          <p className="text-gray-400 text-sm mt-2">Payment not completed</p>
        )}
      </div>
    </div>
  );

  const amountDueSoFar = Math.max(
    Number(orderData.price || 0) - Number(orderData.amountPaid || 0),
    0,
  );

  const ManualPaymentSection = (
    <div className="mt-4 border-t border-gray-700 pt-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm text-gray-400">
            Collected online so far: ₹{orderData.amountPaid || 0} &nbsp;|
            &nbsp; Pending: ₹{amountDueSoFar}
          </p>
          {orderData.adminRecordedAmount > 0 && (
            <p className="text-xs text-amber-400">
              of which ₹{orderData.adminRecordedAmount} was manually recorded
              by an admin
            </p>
          )}
          <p className="text-xs text-gray-500">
            Use this if the customer paid via the admin's own QR — it won't
            show up automatically since it's not tied to this booking.
          </p>
        </div>
        {!showManualPaymentForm && amountDueSoFar > 0 && (
          <div className="flex gap-2 flex-wrap">
            {/* A link made here records itself on this booking when paid;
                one made in the Razorpay dashboard never does. */}
            <a
              href={`/admin/razorpay-payments?bookingId=${encodeURIComponent(
                orderData.bookingId || "",
              )}`}
              target="_blank"
              rel="noreferrer"
              className="bg-[#FBB034] text-black px-3 py-1.5 rounded-md text-sm font-semibold"
            >
              Generate Razorpay Link
            </a>
            <button
              onClick={() => setShowManualPaymentForm(true)}
              className="border border-[#FBB034] text-[#FBB034] px-3 py-1.5 rounded-md text-sm font-semibold"
            >
              + Record Online Payment
            </button>
          </div>
        )}
      </div>

      {showManualPaymentForm && (
        <div className="bg-[#0c0b0b] rounded-md p-3 mt-3 flex flex-col gap-2">
          <div className="flex gap-3 flex-wrap">
            <div>
              <SmallLabel>Amount (₹)</SmallLabel>
              <input
                type="number"
                min="0"
                max={amountDueSoFar}
                value={manualPaymentAmount}
                onChange={(e) => setManualPaymentAmount(e.target.value)}
                placeholder={`up to ${amountDueSoFar}`}
                className="bg-[#1a1a1a] text-white px-2 py-1 border border-gray-600 rounded w-32"
              />
            </div>
            <div>
              <SmallLabel>Razorpay Ref / UTR (optional)</SmallLabel>
              <input
                type="text"
                value={manualPaymentReference}
                onChange={(e) => setManualPaymentReference(e.target.value)}
                placeholder="pay_XXXXXXXX"
                className="bg-[#1a1a1a] text-white px-2 py-1 border border-gray-600 rounded w-48"
              />
            </div>
            <div className="flex-1 min-w-[150px]">
              <SmallLabel>Note (optional)</SmallLabel>
              <input
                type="text"
                value={manualPaymentNote}
                onChange={(e) => setManualPaymentNote(e.target.value)}
                placeholder="e.g. paid via shop QR"
                className="bg-[#1a1a1a] text-white px-2 py-1 border border-gray-600 rounded w-full"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-1">
            <button
              onClick={handleRecordManualPayment}
              disabled={recordingPayment}
              className={`bg-[#FBB034] text-black px-4 py-1.5 rounded-md text-sm font-bold ${
                recordingPayment ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {recordingPayment ? "Saving..." : "Save Payment"}
            </button>
            <button
              onClick={() => setShowManualPaymentForm(false)}
              className="text-gray-400 px-4 py-1.5 rounded-md text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const ServicesEditor = (
    <div className="flex flex-col gap-2 mt-4">
      <input
        type="text"
        placeholder="Search services..."
        value={serviceSearchTerm}
        onChange={(e) => setServiceSearchTerm(e.target.value)}
        className="bg-[#0c0b0b] text-white border-b border-[#E0E0E0] px-2 py-1 w-full"
      />

      {servicesLoading ? (
        <p className="text-gray-400 text-center">Loading services...</p>
      ) : (
        <div className="flex flex-wrap gap-2 mt-2">
          {filteredServices.map((service) => (
            <button
              key={service.id}
              onClick={() => handleServiceAdd(service)}
              className={`px-3 py-1 rounded ${
                selectedServices.find((s) => s.id === service.id)
                  ? "bg-[#FBB034] text-black"
                  : "bg-[#0c0b0b] text-white border border-[#E0E0E0]"
              }`}
            >
              {service.name} (₹{service.price})
            </button>
          ))}
        </div>
      )}

      {selectedServices.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selectedServices.map((service) => (
            <div
              key={service.id}
              className="flex items-center px-2 py-1 bg-[#FBB034] text-black rounded"
            >
              {service.name} (x{service.count})
              <button
                onClick={() => handleServiceRemove(service.id)}
                className="ml-2 text-red-600"
              >
                ❌
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const PriceAdjustment = (
    <div className="mt-6">
      <SmallLabel>Admin Price Adjustment (₹):</SmallLabel>
      <input
        type="text"
        value={manualPriceAdj}
        onChange={(e) => {
          const value = e.target.value;
          // allow negative/positive integers
          if (/^-?\d*$/.test(value)) setManualPriceAdj(value);
        }}
        className="bg-[#0c0b0b] text-white px-2 py-1 border border-gray-600 rounded w-28 sm:w-1/4"
        placeholder="e.g. -100 or +200"
      />

      <div className="mt-4">
        <SmallLabel>Extra Travel Kilometers:</SmallLabel>
        <input
          type="number"
          step="0.1"
          value={extraKilometers}
          onChange={(e) => setExtraKilometers(e.target.value)}
          className="bg-[#0c0b0b] text-white px-2 py-1 border border-gray-600 rounded w-28 sm:w-1/4"
          placeholder="e.g. 10.5"
        />
        {/* What the professional is paid for it (travel allowance) */}
        {orderData?.travelCommission > 0 && (
          <div className="text-xs text-gray-400 mt-1">
            Travel allowance on this booking: ₹{orderData.travelCommission}
            {orderData.extraKilometers ? ` (${orderData.extraKilometers} km)` : ""}
          </div>
        )}
        {extraKilometers !== "" &&
          Number(extraKilometers) !== Number(orderData?.extraKilometers || 0) && (
            <div className="text-xs text-yellow-400 mt-1">
              Travel allowance will be recalculated on save
            </div>
          )}
      </div>

      {manualPriceAdj !== "" && Number(manualPriceAdj) !== 0 && (
        <>
          <SmallLabel className="mt-3">Price Adjustment Reason:</SmallLabel>
          <textarea
            value={adminPriceAdjustmentReason}
            onChange={(e) => setPriceAdjustmentReason(e.target.value)}
            placeholder="Reason for adjustment"
            className="bg-[#0c0b0b] text-white px-2 py-1 border border-gray-600 rounded w-full h-20 mt-2"
          />
        </>
      )}
    </div>
  );

  const SummaryCard = (
    <div className="text-white text-sm bg-[#1a1a1a] p-3 rounded-md">
      <p>Total (Auto-calculated): ₹{calculateNewTotal()}</p>
      {Number(orderData.couponDiscount) > 0 && (
        <p>
          Coupon Discount: ₹{Number(orderData.couponDiscount)}{" "}
          <span className="text-gray-400">
            ({orderData.couponCode || "No Code"})
          </span>
        </p>
      )}
      {Number(orderData.walletAmountUsed) > 0 && (
        <p>
          Wallet Discount: ₹{Number(orderData.walletAmountUsed)}{" "}
          <span className="text-gray-400">(Wallet amount applied by user)</span>
        </p>
      )}
      {Number(orderData.codCharges) > 0 && (
        <p>
          COD Charges: ₹{Number(orderData.codCharges)}{" "}
          <span className="text-gray-400">(Pay after service charges)</span>
        </p>
      )}
      <p>Adjusted Total: ₹{adjustedTotal}</p>
    </div>
  );

  const AuditLogsSection = (
    <SectionCard title="Booking Audit History">
      {auditLoading ? (
        <p className="text-gray-400">Loading history...</p>
      ) : auditLogs.length === 0 ? (
        <p className="text-gray-500">No audit logs available.</p>
      ) : (
        auditLogs.map((log) => (
          <div
            key={log.id}
            className="border-b border-gray-700 pb-3 mb-3 last:border-0"
          >
            <p className="text-sm text-gray-400">
              {new Date(log.createdAt).toLocaleString()}
            </p>
            <p className="font-semibold">{log.reason}</p>

            {log.details?.priceChange && (
              <p className="text-sm mt-1">
                💰 Price: ₹{log.details.priceChange.old} → ₹
                {log.details.priceChange.new} (
                {log.details.priceChange.difference})
              </p>
            )}
            {log.details?.statusChange && (
              <p className="text-sm mt-1">
                🔄 Status: {log.details.statusChange.old} →{" "}
                {log.details.statusChange.new}
              </p>
            )}
            {log.details?.paymentTypeChange && (
              <p className="text-sm mt-1">
                💳 Payment: {log.details.paymentTypeChange.old} →{" "}
                {log.details.paymentTypeChange.new}
              </p>
            )}
            {log.details?.newServices?.length > 0 && (
              <p className="text-sm mt-1">
                ➕ Added:{" "}
                {log.details.newServices.map((s) => s.name).join(", ")}
              </p>
            )}
            {log.details?.removedServiceIds?.length > 0 && (
              <p className="text-sm mt-1 text-red-400">
                ➖ Removed: {log.details.removedServiceIds.join(", ")}
              </p>
            )}
            {log.details?.source === "ADMIN_MANUAL_QR" && (
              <p className="text-sm mt-1 text-green-400">
                📱 Recorded ₹{log.details.amount} online (admin QR)
                {log.details.referenceId
                  ? ` — Ref: ${log.details.referenceId}`
                  : ""}
                {log.details.note ? ` — "${log.details.note}"` : ""}
              </p>
            )}
          </div>
        ))
      )}
    </SectionCard>
  );

  /* -------------------
     Render variants
     ------------------- */
  // if (isVerified) {
  //   return (
  //     <div className="flex flex-col space-y-6">
  //       <SectionCard title="Verification Status">
  //         <div className="bg-[#1a1a1a] text-white p-6 rounded-md text-center">
  //           <h3 className="text-[#FBB034] text-lg font-bold mb-3">
  //             Verification Status
  //           </h3>
  //           <p className="text-xl font-semibold mb-2">
  //             Current Status: {orderData.verificationStatus}
  //           </p>
  //           <p className="text-gray-400 text-sm">
  //             Verification already completed. You cannot modify this booking
  //             now.
  //           </p>
  //         </div>
  //       </SectionCard>

  //       {AuditLogsSection}
  //     </div>
  //   );
  // }

  if (isPaymentPending) {
    return (
      <div className="flex flex-col space-y-6">
        {Header}

        <SectionCard>
          {PaymentSection}
          {ManualPaymentSection}
        </SectionCard>
        <div className="ml-auto">
          <button
            onClick={handleSubmit}
            disabled={verifying}
            className={`bg-[#FBB034] text-black px-6 py-2 rounded-md font-bold ${
              verifying ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            {verifying ? "Verifying..." : "Verify & Save"}
          </button>
        </div>

        {AuditLogsSection}
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6">
      {Header}

      <SectionCard>
        {PaymentSection}
        {ManualPaymentSection}
      </SectionCard>

      <SectionCard>
        <div className="grid grid-cols-1 gap-3">
          <div>
            <SmallLabel>Address:</SmallLabel>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Enter/modify address"
              className="bg-[#0c0b0b] text-white px-2 py-1 border border-gray-600 rounded w-full h-20"
            />
          </div>

          <div>
            <SmallLabel>Pincode:</SmallLabel>
            <input
              type="text"
              value={pincode}
              onChange={(e) => {
                const v = e.target.value;
                if (/^\d{0,6}$/.test(v)) setPincode(v);
              }}
              placeholder="e.g. 110001"
              className="bg-[#0c0b0b] text-white px-2 py-1 border border-gray-600 rounded w-28 sm:w-1/4"
            />
          </div>
        </div>

        {ServicesEditor}

        {PriceAdjustment}

        {SummaryCard}

        <div className="flex justify-center py-6">
          <button
            onClick={handleSubmit}
            disabled={verifying}
            className={`bg-[#FBB034] text-black px-6 py-2 rounded-md font-bold ${
              verifying ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            {verifying ? "Verifying..." : "Verify & Save"}
          </button>
        </div>
      </SectionCard>

      {AuditLogsSection}
    </div>
  );
};

export default VerifyBookingForm;
